import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { throttle } from 'underscore';
import { prop } from 'ramda';
import { useDeviceSelector } from 'js/store/hooks';
import { getClassNames } from 'js/utils/classNames.util';
import { useUpdateEffect } from 'js/hooks/useUpdateEffect';
import buildClassName from 'js/utils/classNameBuilder.util';

import { DEFAULT_LINE_SPEED } from './constants';

import style from './InfiniteLine.scss';

/**
 * @typedef {Object} ClassNames
 * @property [wrapper] {string}
 * @property [line] {string}
 * @property [repeatItem] {string}
 * @property [separator] {string}
 */

/**
 * @param props {Object}
 * @param [props.separator] {string} - between repeat items
 * @param [props.direction="left"] {string} - "right" for reverse direction
 * @param [props.pauseOnHover=true] {boolean} - pause line animation on hover for desktop
 * @param [props.removeAnimation=false] {boolean} - force stop line animation
 * @param [props.speed=30] {number}
 * @param [props.propsClassNames] {ClassNames}
 */
export const InfiniteLine = (props) => {
    const {
        children,
        separator = '',
        direction = 'left',
        pauseOnHover = true,
        removeAnimation = false,
        speed = DEFAULT_LINE_SPEED,
        classNames: propsClassNames,
        stopIfNotEnoughContent = false
    } = props;
    const wrapperRef = useRef();
    const repeatItemRef = useRef();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const classNames = useMemo(() => propsClassNames || {}, [propsClassNames]);
    const [repeatsNumber, setRepeatsNumber] = useState(1);
    const [resetAnimation, setResetAnimation] = useState(false);
    const [isAnimated, setIsAnimated] = useState(false);
    const [itemSpeed, setItemSpeed] = useState(speed);
    const [isFocusedByTab, setIsFocusedByTab] = useState(false);

    const updateLine = useCallback(() => {
        if (!repeatItemRef.current || !wrapperRef.current) {
            return;
        }

        const { width: repeatItemWidth } = repeatItemRef.current.getBoundingClientRect();
        const { width: wrapperWidth } = wrapperRef.current.getBoundingClientRect();
        const newRepeatsNumber = Math.ceil(wrapperWidth / repeatItemWidth) + 1;

        if (stopIfNotEnoughContent && repeatItemWidth <= wrapperWidth) {
            setResetAnimation(true);
            setRepeatsNumber(1);

            return;
        }

        setIsAnimated(true);
        setResetAnimation(true);
        setRepeatsNumber(newRepeatsNumber);
        setItemSpeed(repeatItemWidth / speed);

        // reset animation because of it be out of sync while resize to avoid random items overlapping or flickering
        setTimeout(() => setResetAnimation(false), 50);
    }, [wrapperRef, speed, stopIfNotEnoughContent]);

    useEffect(() => updateLine(), [updateLine]);

    useUpdateEffect(() => {
        setResetAnimation(!removeAnimation);
    }, [removeAnimation]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onResize = useCallback(throttle(updateLine, 500),
        [updateLine]
    );

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const resizeObserver = new ResizeObserver(onResize);

        resizeObserver.observe(wrapper);

        return () => resizeObserver.unobserve(wrapper);
    }, [wrapperRef, onResize]);

    // region Accessibility

    const onKeyUp = useCallback((event) => {
        if (!isDesktop || event.key !== 'Tab') {
            return;
        }

        if (isAnimated && !resetAnimation) {
            setResetAnimation(true);
            setIsFocusedByTab(true);
        }
    }, [isDesktop, isAnimated, resetAnimation]);

    const onBlur = useCallback(() => {
        if (!isDesktop || !isFocusedByTab) {
            return;
        }

        // Give browser time to focus the next element
        requestAnimationFrame(() => {
            if (isAnimated && !wrapperRef.current?.contains(document.activeElement)) {
                wrapperRef.current?.scrollTo({ left: 0 });
                updateLine();
                setIsFocusedByTab(false);
            }
        });
    }, [isAnimated, isFocusedByTab, isDesktop, wrapperRef, updateLine]);

    // endregion

    const lineClassNames = getClassNames([
        buildClassName(['line'], style, classNames),
        isAnimated && style.animated,
        resetAnimation && style.resetAnimation,
        pauseOnHover && style.pauseOnHover
    ]);

    return (
        <div
            onBlur={onBlur}
            ref={wrapperRef}
            onKeyUp={onKeyUp}
            className={buildClassName(['wrapper'], style, classNames)}
        >
            <div className={lineClassNames}>
                {[...Array(repeatsNumber)].map((_, index) => (
                    <div
                        key={index}
                        ref={index === 0 ? repeatItemRef : null}
                        className={buildClassName(['repeatItem'], style, classNames)}
                        style={{
                            animationDuration: `${itemSpeed}s`,
                            animationDirection: direction === 'right' ? 'reverse' : 'normal'
                        }}
                    >
                        {children}
                        {repeatsNumber > 1 && separator && (
                            <span className={buildClassName(['separator'], style, classNames)}>{separator}</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
